import type {
  PreClientListResponse,
  PreClientQueryParams,
  PreClientQueryParamsInternal,
  RejectedClientQueryParams,
  RejectedClientQueryParamsInternal,
  ApprovedClientQueryParams,
  ApprovedClientQueryParamsInternal,
  PreClientDetail,
  DocumentUrlResponse,
} from '@/features/onboarding/types/preClient.types';
import { PreClientStatus as Status } from '@/features/onboarding/types/preClient.types';
import { apiClient, handleAPIError } from '@/shared/utils/api/client';

/**
 * API route for pre-clients
 */
const PRE_CLIENTS_ENDPOINT = '/pre-clients';

/**
 * API route for rejection categories
 */
const REJECTION_CATEGORIES_ENDPOINT = '/pre-clients/rejection-categories';

/**
 * Build query string for pre-analysis (status 3)
 */
const buildPreAnalysisQueryParams = (params: PreClientQueryParamsInternal): URLSearchParams => {
  const queryParams = new URLSearchParams();

  queryParams.append('status', params.status.toString());

  if (params.page) {
    queryParams.append('page', params.page.toString());
  }

  if (params.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }

  if (params.dateSearch?.trim()) {
    queryParams.append('dateSearch', params.dateSearch.trim());
  }

  return queryParams;
};

/**
 * Build query string for rejected clients (status 1)
 */
const buildRejectedQueryParams = (params: RejectedClientQueryParamsInternal): URLSearchParams => {
  const queryParams = new URLSearchParams();

  queryParams.append('status', params.status.toString());

  if (params.page) {
    queryParams.append('page', params.page.toString());
  }

  if (params.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }

  if (params.dateStart?.trim()) {
    queryParams.append('dateStart', params.dateStart.trim());
  }

  if (params.dateEnd?.trim()) {
    queryParams.append('dateEnd', params.dateEnd.trim());
  }

  if (params.typeDisaproved) {
    queryParams.append('typeDisaproved', params.typeDisaproved.toString());
  }

  if (params.categoryRejectedName?.trim()) {
    queryParams.append('categoryRejectedName', params.categoryRejectedName.trim());
  }

  return queryParams;
};

/**
 * Build query string for approved clients (status 2)
 */
const buildApprovedQueryParams = (params: ApprovedClientQueryParamsInternal): URLSearchParams => {
  const queryParams = new URLSearchParams();

  queryParams.append('status', params.status.toString());

  if (params.page) {
    queryParams.append('page', params.page.toString());
  }

  if (params.limit) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }

  if (params.dateStart?.trim()) {
    queryParams.append('dateStart', params.dateStart.trim());
  }

  if (params.dateEnd?.trim()) {
    queryParams.append('dateEnd', params.dateEnd.trim());
  }

  return queryParams;
};

/**
 * Pre-client Service
 * Handles all pre-client related API calls
 */
export const preClientService = {
  /**
   * Get paginated list of pre-clients in analysis (status = 3)
   * @param params - Query parameters (page, limit, search, dateSearch)
   * @returns Promise with pre-client list response
   */
  getPreAnalysisList: async (params: PreClientQueryParams = {}): Promise<PreClientListResponse> => {
    try {
      const internalParams: PreClientQueryParamsInternal = {
        ...params,
        status: Status.IN_ANALYSIS,
      };

      const queryParams = buildPreAnalysisQueryParams(internalParams);
      const url = `${PRE_CLIENTS_ENDPOINT}?${queryParams.toString()}`;

      const response = await apiClient.get<PreClientListResponse>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Get paginated list of rejected pre-clients (status = 1)
   * @param params - Query parameters (page, limit, search, dateStart, dateEnd, rejectionType, user, reason)
   * @returns Promise with pre-client list response
   */
  getRejectedList: async (
    params: RejectedClientQueryParams = {}
  ): Promise<PreClientListResponse> => {
    try {
      const internalParams: RejectedClientQueryParamsInternal = {
        ...params,
        status: Status.REJECTED,
      };

      const queryParams = buildRejectedQueryParams(internalParams);
      const url = `${PRE_CLIENTS_ENDPOINT}?${queryParams.toString()}`;

      const response = await apiClient.get<PreClientListResponse>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Get paginated list of approved pre-clients (status = 2)
   * @param params - Query parameters (page, limit, search, dateStart, dateEnd, approvalType, user)
   * @returns Promise with pre-client list response
   */
  getApprovedList: async (
    params: ApprovedClientQueryParams = {}
  ): Promise<PreClientListResponse> => {
    try {
      const internalParams: ApprovedClientQueryParamsInternal = {
        ...params,
        status: Status.APPROVED,
      };

      const queryParams = buildApprovedQueryParams(internalParams);
      const url = `${PRE_CLIENTS_ENDPOINT}?${queryParams.toString()}`;

      const response = await apiClient.get<PreClientListResponse>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Get pre-client details by ID
   * Works for all statuses (rejected, approved, in analysis)
   * @param id - Pre-client ID
   * @returns Promise with pre-client detail response
   */
  getById: async (id: string): Promise<PreClientDetail> => {
    try {
      const url = `${PRE_CLIENTS_ENDPOINT}/${id}`;
      const response = await apiClient.get<PreClientDetail>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Get document URL for viewing/downloading
   * Returns a temporary S3 signed URL
   * @param documentId - Document ID
   * @returns Promise with document URL response
   */
  getDocumentUrl: async (documentId: string): Promise<DocumentUrlResponse> => {
    try {
      const url = `${PRE_CLIENTS_ENDPOINT}/documents/${documentId}/url`;
      const response = await apiClient.get<DocumentUrlResponse>(url);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },

  /**
   * Get rejection categories list
   * @returns Promise with array of category names
   */
  getRejectionCategories: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<string[]>(REJECTION_CATEGORIES_ENDPOINT);
      return response.data;
    } catch (error) {
      throw handleAPIError(error);
    }
  },
};
